// Detection Events
export enum DetectionType {
  WEBRTC = 'webrtc',
  ECDN11 = 'ecdn11',
  PLATFORM = 'platform', // Desktop version for teams.
  RUNTIME = 'runtime',
  REPORTER = 'reporter',
  NO_PLAYBACK = 'noPlaybackMethod'
}

export enum PrimingType {
  PREFETCH = 'prefetch',
  PUBLISH = 'publish',
  PUBLISH_CHECK = 'publishCheck',
  SESSION = 'session',
  RESTART = 'restart'
}

export enum ComponentCode {
  CONTENT = 2000,
  ELEVEN_AGENT = 2001,
  SDK = 2002,
  SIGNAL = 2003,
  STATS = 2004,
  TEN_AGENT = 2005,
  WEBRTC_STATS = 2006
}

export enum StatusCode {
  CONNECTION_ERROR = 3000,
  NO_PLAYBACK_FOUND = 3001
}

export enum ReportingState {
  CLEANED, // Reporter has cleaned up because of transition from page
  ENDED, // Received an ended event and sent a report (sends report immediately).
  PAUSED, // Received a pause event and sent a report.
  PAUSING, // Received a pause event but not yet sent a report.
  PLAY, // Received a play event from a paused state or unstarted state
  PLAYING, // Received a playing event and sending reports.
  RESTART, // Was in an ENDED state and received a seeking or play event.
  WAITING // We've just started out and have not received a play event.
}

export interface RuntimeDetectionEventData {
  componentCode: ComponentCode;
  error?: {
    statusCode: number;
  };
}

//#region "Player" events
// See: https://kollective.atlassian.net/wiki/spaces/ECDN11/pages/1857421342
export interface NoMetadataEvent {
  s?: true;
  t?: number;
  ts: number;
  e: number;
}

export interface IAudioTrackInfoEvent extends NoMetadataEvent {
  at: TrackInfo;
}

export interface IBitrateEvent extends NoMetadataEvent {
  b: number;
}

export interface ICaptionTrackInfoEvent extends NoMetadataEvent {
  ct: TrackInfo;
}

export interface IClientResolutionEvent extends NoMetadataEvent {
  cr: string;
}

export interface IDurationEvent extends NoMetadataEvent {
  dur: number;
  synthetic?: boolean;
}

export interface IFocusEvent extends NoMetadataEvent {
  foc: boolean;
}

export interface IFullscreenEvent extends NoMetadataEvent {
  full: boolean;
}

export interface IMuteEvent extends NoMetadataEvent {
  mute: boolean;
}

export interface IPictureInPictureEvent extends NoMetadataEvent {
  pip: boolean;
}

export interface IPlaybackRateEvent extends NoMetadataEvent {
  rate: number;
}

export interface IPositionEvent extends NoMetadataEvent {
  pos: number;
}

export interface IVideoResolutionEvent extends NoMetadataEvent {
  vr: string;
}

export interface IVisibilityEvent extends NoMetadataEvent {
  vis: VisibilityState;
}

export interface TrackInfo {
  lang: string;
  name: string;
}

export enum VisibilityState {
  HIDDEN = 0,
  VISIBLE = 1
}

export interface TechApiState {
  lastBitrate?: number;
  at?: TrackInfo;
  ct?: TrackInfo;
}

export type TrackType = 'at' | 'ct';

// See: common/Reporter/constants.ts: MonitoredMediaEvents and ConstructedEvents
export type PlayerEvent =
  | NoMetadataEvent
  | IAudioTrackInfoEvent
  | IBitrateEvent
  | ICaptionTrackInfoEvent
  | IClientResolutionEvent
  | IDurationEvent
  | IFocusEvent
  | IFullscreenEvent
  | IMuteEvent
  | IPictureInPictureEvent
  | IPlaybackRateEvent
  | IPositionEvent
  | IVideoResolutionEvent
  | IVisibilityEvent;

export interface BasePlayerEvent {
  e: number; // event code
  at?: TrackInfo; // audio track information
  b?: number; // current bitrate
  cr?: string; // client video resolution
  ct?: TrackInfo; // caption track information
  dur?: number; // duration of the video/audio stream
  foc?: boolean; // isFocused
  full?: boolean; // is fullscreen
  mute?: boolean; // is muted
  pip?: boolean; // is the player in picture in picture
  pos?: number; // position in the video/audio stream
  rate?: number; // speed at which the player is playing
  synthetic?: boolean; // locally made event that shouldn't bubble into reports
  vis?: VisibilityState;
  vr?: string; // current video resolution
  s?: true; // synthetic event sent to the back-end
  t?: number;
  ts?: number;
}
//#endregion "Player" events

export enum PrimingError {
  DISABLED = 'disabled',
  MISSING_DATA = 'missingData',
  NO_PLAYBACK_METHOD = 'noValidPlaybackMethodFound',
  NOT_CAPABLE = 'notDeliveryCapable',
  UNKNOWN = 'unknown',
  UNSUPPORTED = 'unsupportedBrowser'
}

export enum DetectionReason {
  IMMEDIATE = 'sendImmediateReport',
  INTERVAL = 'intervalUpdate',
  NO_PLAY_EVENT = 'noPlayEvent',
  PAUSING = 'pausing',
  START = 'start',
  STOP = 'stop',
  TIMEOUT_SET = 'timeoutSet',
  TRIMMED = 'trimmed',
  UPDATE_AGENT = '11agentUpdate',
  VISIBILITY_CHANGE = 'visibilitychange'
}

export interface LastDetectionEvent {
  v?: string;
  date?: number;
  type?: DetectionType;
}

export interface RuntimeDetectionEvent {
  data?: RuntimeDetectionEventData;
  lastDetection?: LastDetectionEvent;
  method?: string;
  reason?: PrimingError | DetectionReason;
  status?: string | number;
  success: boolean;
  t?: number;
  type: DetectionType;
  v?: string;
}
export interface GeneralDetectionEvent {
  type: DetectionType;
  data?: Record<string, unknown>;
  t?: number;
}

export type DetectionEvent = GeneralDetectionEvent | RuntimeDetectionEvent;

export interface PrimingActionEvent {
  type: PrimingType;
  status?: string;
  t?: number;
}

export interface PrimingProgressEvent {
  progress: number;
  t?: number;
}

export interface PrimingCmdEvent {
  cmd: 'httpError' | 'handleError' | 'cancelStream' | 'switchToCDN';
  t?: number;
}

export type PrimingEvent =
  | PrimingActionEvent
  | PrimingProgressEvent
  | PrimingCmdEvent;
